import { useState } from "react";
import { siteInitials } from "@/lib/utils";

export function Preloader({
  faviconUrl,
  siteName,
}: {
  faviconUrl?: string | null;
  /**
   * The site's display name, used only for the badge below when there is no usable
   * icon. Optional so the overlay still renders correctly before `/api/seo` answers.
   */
  siteName?: string | null;
}) {
  const iconUrl = String(faviconUrl || "").trim();
  /**
   * The URL that failed rather than a boolean, matching `Navbar`'s avatar/logo
   * handling: a newly uploaded icon is still attempted after an older one 404s.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showIcon = !!iconUrl && failedUrl !== iconUrl;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0a101f]">
      <div className="flex flex-col items-center gap-6 px-6 text-center">
        <div className="relative flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-gradient-to-br from-primary via-yellow-400 to-primary shadow-[0_0_50px_rgba(234,179,8,0.22)]">
          <div className="absolute inset-0 animate-pulse rounded-[1.75rem] bg-primary/20 blur-xl" />
          {showIcon ? (
            <img
              src={iconUrl}
              alt="Site icon"
              className="relative h-10 w-10 object-contain"
              loading="eager"
              decoding="async"
              onError={() => setFailedUrl(iconUrl)}
            />
          ) : (
            /*
             * The badge fallback.
             *
             * This used to be a literal `FX` — the previous site's branding — so an
             * instance with no favicon upload, or one whose icon file 404s (note the
             * `onError` above now routes that case here rather than leaving a broken
             * image), advertised the wrong brand for the first 200 ms of every visit.
             * It renders the site's own initials instead: "BullBuzz" → "BB".
             */
            <span className="relative text-3xl font-black text-black">{siteInitials(siteName)}</span>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xl font-black uppercase tracking-[0.18em] text-white">Loading Market Hub</div>
          <div className="text-sm font-medium text-gray-500">Preparing brokers, bonuses, news, and tools...</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:-0.2s]" />
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/80 [animation-delay:-0.1s]" />
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" />
        </div>
      </div>
    </div>
  );
}
